/**
 * Promotional discount visibility.
 *
 * Reveals promotional UI (top bar, product badges, price notes, cart message)
 * when Shopify has a discount code attached to the visitor's session that
 * matches a configured campaign. The code is read from Shopify's own cart
 * state (/cart.js -> discount_codes), so it activates when a customer arrives
 * via a native discount link like /discount/AMORE15.
 *
 * This is VISUAL ONLY — Shopify remains the source of truth for whether the
 * discount actually applies at cart/checkout. Per-product badge eligibility
 * (scope/tags/collections) is theme-configured, since Shopify does not expose
 * per-product discount scope to the storefront.
 *
 * Configuration is provided by snippets/promo-config.liquid as
 * window.ThemePromoConfig.
 */
(function () {
  'use strict';

  var cfg = window.ThemePromoConfig;
  if (!cfg || !cfg.enabled) return;

  var CLOSED_KEY = 'themePromoBarClosed';

  // Cached active-campaign state, derived from Shopify's applied discount codes.
  var activeCode = null; // uppercase code string, or null
  var activeApplicable = false; // whether Shopify says the code applies to the current cart

  /* ----------------------------------------------------------- campaign map */

  function campaignFor(code) {
    if (!code) return null;
    return cfg.campaigns[String(code).toUpperCase()] || null;
  }

  function activeCampaign() {
    return campaignFor(activeCode);
  }

  /* ---------------------------------------------------- read Shopify state */

  function refreshFromCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        var match = null;
        for (var i = 0; i < codes.length; i++) {
          var dc = codes[i];
          if (!campaignFor(dc.code)) continue; // only configured campaigns trigger visuals
          // Prefer an applicable code over a merely-attached one.
          if (!match || (dc.applicable && !match.applicable)) match = dc;
        }
        activeCode = match ? String(match.code).toUpperCase() : null;
        activeApplicable = match ? !!match.applicable : false;
      })
      .catch(function () { /* keep previous state on failure */ })
      .then(function () { apply(); });
  }

  /* -------------------------------------------------------------- money fmt */

  function formatMoney(cents, format) {
    var f = format || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function build(precision, thousands, decimal) {
      var num = (cents / 100).toFixed(precision);
      var parts = num.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ''));
      var frac = parts[1] ? (decimal || '.') + parts[1] : '';
      return whole + frac;
    }
    var match = f.match(placeholder);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount': value = build(2, ',', '.'); break;
      case 'amount_no_decimals': value = build(0, ',', ''); break;
      case 'amount_with_comma_separator': value = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = build(0, '.', ''); break;
      case 'amount_with_space_separator': value = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = build(0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = build(2, "'", '.'); break;
      default: value = build(2, ',', '.');
    }
    return f.replace(placeholder, value);
  }

  /* ----------------------------------------------------------- dom helpers */
  /* Only mutate when the value actually changes, so the MutationObserver
     below does not re-trigger itself into an infinite loop. */

  function setText(el, txt) {
    if (el && el.textContent !== txt) el.textContent = txt;
  }
  function setHTML(el, html) {
    if (el && el.innerHTML !== html) el.innerHTML = html;
  }
  function setHidden(el, val) {
    if (el && el.hidden !== val) el.hidden = val;
  }
  function setAttr(el, name, val) {
    if (!el) return;
    if (val === null) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
    } else if (el.getAttribute(name) !== val) {
      el.setAttribute(name, val);
    }
  }

  function codeList(el, attr) {
    return (el.getAttribute(attr) || '')
      .toUpperCase()
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* ------------------------------------------------------------- apply UI */

  function applyBar(camp) {
    var bar = document.querySelector('[data-promo-bar]');
    if (!bar) return;
    var closed = false;
    try {
      closed = sessionStorage.getItem(CLOSED_KEY) === '1';
    } catch (e) {}
    if (camp && !closed) {
      setText(bar.querySelector('[data-promo-bar-message]'), camp.barMessage);
      setHidden(bar, false);
      setAttr(bar, 'data-visible', 'true');
    } else {
      setHidden(bar, true);
      setAttr(bar, 'data-visible', null);
    }
  }

  function applyBadges(camp) {
    var badges = document.querySelectorAll('[data-promo-badge]');
    for (var i = 0; i < badges.length; i++) {
      var el = badges[i];
      var codes = codeList(el, 'data-promo-codes');
      if (camp && codes.indexOf(camp.code) !== -1) {
        setText(el, camp.badgeText);
        setHidden(el, false);
      } else {
        setHidden(el, true);
      }
    }
  }

  function applyPriceNotes(camp) {
    // Product-detail-page "with code" label (there is no card badge on the PDP).
    var notes = document.querySelectorAll('[data-promo-price-note]');
    for (var i = 0; i < notes.length; i++) {
      var el = notes[i];
      var codes = codeList(el, 'data-promo-codes');
      if (!camp || codes.indexOf(camp.code) === -1) {
        setHidden(el, true);
        continue;
      }
      var label = (cfg.priceNoteLabel || '')
        .replace(/\{code\}/g, camp.code)
        .replace(/\{percent\}/g, camp.percentage);
      setText(el.querySelector('[data-promo-note-label]'), label);
      setHidden(el, false);
    }
  }

  // Builds the discounted-preview markup, mirroring the theme's sale price:
  // discounted first (.price), original struck-through (.compare-at-price).
  function buildPreviewHTML(base, camp, format) {
    // Match Shopify's cart/checkout math exactly: it works in integer cents and
    // TRUNCATES the discount amount, i.e. price - floor(price * pct / 100).
    // (e.g. 2199 - floor(329.85) = 1870, not round(2199*0.85) = 1869.)
    var discount = Math.floor((base * camp.percentage) / 100);
    var discounted = base - discount;
    return (
      '<div ref="priceContainer">' +
      '<span class="price">' + formatMoney(discounted, format) + '</span> ' +
      '<span class="compare-at-price">' + formatMoney(base, format) + '</span>' +
      '</div>'
    );
  }

  // Swaps every eligible product-card price to the discounted preview. Reads the
  // base price from data-base-price (never from displayed text), so re-running is
  // idempotent and never double-discounts.
  function applyCardPrices(camp) {
    var wraps = document.querySelectorAll('[data-promo-price]');
    for (var i = 0; i < wraps.length; i++) {
      var el = wraps[i];
      var original = el.querySelector('[data-promo-price-original]');
      var preview = el.querySelector('[data-promo-price-preview]');
      var codes = codeList(el, 'data-promo-codes');
      var base = parseInt(el.getAttribute('data-base-price'), 10);
      var eligible = camp && codes.indexOf(camp.code) !== -1 && base > 0 && camp.percentage > 0;
      if (eligible) {
        var format = el.getAttribute('data-money-format') || cfg.moneyFormat;
        setHTML(preview, buildPreviewHTML(base, camp, format));
        setHidden(preview, false);
        setHidden(original, true);
      } else {
        setHidden(preview, true);
        setHidden(original, false);
      }
    }
  }

  function applyCart(camp) {
    var notes = document.querySelectorAll('[data-promo-cart-note]');
    for (var i = 0; i < notes.length; i++) {
      var el = notes[i];
      if (!camp || !cfg.cartSoftMessage) {
        setHidden(el, true);
        continue;
      }
      // Shopify renders confirmed (allocated) discounts itself; only nudge when
      // the active code is attached but not yet reduced in the cart.
      var applied = codeList(el, 'data-applied-discounts');
      if (applied.indexOf(camp.code) !== -1) {
        setHidden(el, true);
        continue;
      }
      setText(el.querySelector('[data-promo-cart-message]'), cfg.cartSoftMessage);
      setHidden(el, false);
    }
  }

  function apply() {
    var camp = activeCampaign();
    setAttr(document.body, 'data-promo-active', camp ? camp.code : null);
    applyBar(camp);
    applyBadges(camp);
    applyCardPrices(camp);
    applyPriceNotes(camp);
    applyCart(camp);
  }

  /* ------------------------------------------------------------- lifecycle */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-promo-bar-close]');
    if (!btn) return;
    try {
      sessionStorage.setItem(CLOSED_KEY, '1');
    } catch (err) {}
    var bar = btn.closest('[data-promo-bar]');
    if (bar) {
      bar.hidden = true;
      bar.removeAttribute('data-visible');
    }
  });

  // Refresh Shopify cart state after any cart mutation triggered via fetch.
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function () {
      var result = origFetch.apply(this, arguments);
      try {
        var a0 = arguments[0];
        var url = typeof a0 === 'string' ? a0 : a0 && a0.url ? a0.url : '';
        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          result.then(function () { scheduleRefresh(); }).catch(function () {});
        }
      } catch (e) {}
      return result;
    };
  }

  var refreshTimer = null;
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refreshFromCart();
    }, 300);
  }

  var applyScheduled = false;
  function scheduleApply() {
    if (applyScheduled) return;
    applyScheduled = true;
    requestAnimationFrame(function () {
      applyScheduled = false;
      apply();
    });
  }

  function start() {
    refreshFromCart(); // initial Shopify state -> apply()

    if (document.body) {
      new MutationObserver(function (mutations) {
        var cartChanged = false;
        for (var i = 0; i < mutations.length && !cartChanged; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (
              (n.matches &&
                n.matches('cart-items-component, cart-drawer-component, .cart__summary-totals, [data-promo-cart-note]')) ||
              (n.querySelector && n.querySelector('cart-items-component, .cart__summary-totals, [data-promo-cart-note]'))
            ) {
              cartChanged = true;
              break;
            }
          }
        }
        scheduleApply();
        if (cartChanged) scheduleRefresh(); // cart re-rendered (add/remove/discount) -> re-read state
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('pageshow', function () { refreshFromCart(); });
  document.addEventListener('shopify:section:load', apply);

  // Variant change: the theme swaps only the inner [ref="priceContainer"], which
  // leaves our promo-price wrapper's data-base-price (and the discounted preview)
  // stale. Read the fresh base price / eligibility from the re-rendered section
  // in the event payload and re-apply so the preview tracks the selected variant.
  document.addEventListener('variant:update', function (e) {
    try {
      var data = (e.detail && e.detail.data) || {};
      var html = data.html;
      if (html && html.querySelector) {
        var fresh = html.querySelector('[data-promo-price]');
        if (fresh) {
          var pid = data.productId;
          var selector = pid
            ? 'product-price[data-product-id="' + pid + '"] [data-promo-price]'
            : '[data-promo-price]';
          var live = document.querySelectorAll(selector);
          for (var i = 0; i < live.length; i++) {
            var base = fresh.getAttribute('data-base-price');
            if (base != null) live[i].setAttribute('data-base-price', base);
            var codes = fresh.getAttribute('data-promo-codes');
            if (codes != null) live[i].setAttribute('data-promo-codes', codes);
            var fmt = fresh.getAttribute('data-money-format');
            if (fmt != null) live[i].setAttribute('data-money-format', fmt);
          }
        }
      }
    } catch (err) {}
    // Re-apply on a macrotask so it runs AFTER the theme's own rAF-based price
    // update for this variant (a same-frame re-apply would be overwritten,
    // leaving the preview one variant behind). The second, later call is a
    // safety net for slower section updates.
    setTimeout(apply, 0);
    setTimeout(apply, 200);
  });

  window.ThemePromo = {
    apply: apply,
    refresh: refreshFromCart,
    getActive: activeCampaign,
    isApplicable: function () { return activeApplicable; },
  };
})();
